import { useState, useRef, useEffect, useCallback, type ReactNode, type KeyboardEvent } from 'react';
import { motion } from 'framer-motion';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { SiriOrb } from '@/components/ui/siri-orb';
import { cn } from '@/lib/utils';
import {
  ImageIcon,
  FileUp,
  MonitorIcon,
  CircleUserRound,
  ArrowUpIcon,
  Paperclip,
  Code2,
  Palette,
  Layers,
  Rocket,
} from 'lucide-react';

/** Night sky — Unsplash (reliable CDN) */
const MOON_BG =
  'https://images.unsplash.com/photo-1446944342999-8844bba01e1c?auto=format&fit=crop&w=2400&q=80';

interface AutoResizeProps {
  minHeight: number;
  maxHeight?: number;
}

function useAutoResizeTextarea({ minHeight, maxHeight }: AutoResizeProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustHeight = useCallback(
    (reset?: boolean) => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      if (reset) {
        textarea.style.height = `${minHeight}px`;
        return;
      }

      textarea.style.height = `${minHeight}px`;
      const newHeight = Math.max(
        minHeight,
        Math.min(textarea.scrollHeight, maxHeight ?? Number.POSITIVE_INFINITY)
      );
      textarea.style.height = `${newHeight}px`;
    },
    [minHeight, maxHeight]
  );

  useEffect(() => {
    if (textareaRef.current) textareaRef.current.style.height = `${minHeight}px`;
  }, [minHeight]);

  return { textareaRef, adjustHeight };
}

const BACKR_QUICK_PROMPTS: { label: string; icon: ReactNode; prompt: string }[] = [
  { label: 'Find investors', icon: <Code2 className="h-4 w-4" />, prompt: 'Help me find seed-stage investors who fund B2B SaaS in healthcare.' },
  { label: 'Outreach email', icon: <Rocket className="h-4 w-4" />, prompt: 'Draft a short, personal cold email to a VC partner introducing my startup.' },
  { label: 'Meeting prep', icon: <Layers className="h-4 w-4" />, prompt: 'What should I research before a first call with an investor?' },
  { label: 'Pitch angles', icon: <Palette className="h-4 w-4" />, prompt: 'Suggest three concise ways to frame my traction for investors.' },
  { label: 'Raise plan', icon: <CircleUserRound className="h-4 w-4" />, prompt: 'Outline a simple timeline for my next fundraising round.' },
  { label: 'Landing copy', icon: <MonitorIcon className="h-4 w-4" />, prompt: 'Help me tighten the one-liner on my investor-facing landing page.' },
  { label: 'Data room', icon: <FileUp className="h-4 w-4" />, prompt: 'What documents should I prepare before sharing a data room?' },
  { label: 'Deck visuals', icon: <ImageIcon className="h-4 w-4" />, prompt: 'What slides do seed investors care about most in a first deck?' },
];

export interface RuixenMoonChatProps {
  onSubmit: (message: string) => void;
  className?: string;
  /** Shorter hero, smaller type — for Flow panel / narrow layouts */
  compact?: boolean;
  /** Defaults: RaiseBeacon AI / fundraising tagline */
  heroTitle?: string;
  heroSubtitle?: string;
}

export function RuixenMoonChat({
  onSubmit,
  className,
  compact = false,
  heroTitle = 'RaiseBeacon AI',
  heroSubtitle = 'Build something amazing — just start typing below.',
}: RuixenMoonChatProps) {
  const [message, setMessage] = useState('');
  const { textareaRef, adjustHeight } = useAutoResizeTextarea({
    minHeight: 48,
    maxHeight: 150,
  });

  const send = () => {
    const t = message.trim();
    if (!t) return;
    onSubmit(t);
    setMessage('');
    adjustHeight(true);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div
      className={cn(
        'relative flex w-full flex-col items-center overflow-hidden rounded-2xl border border-white/10 bg-slate-950 shadow-2xl',
        compact ? 'min-h-[220px]' : 'min-h-[560px] flex-1',
        className
      )}
      style={{
        backgroundImage: `linear-gradient(to bottom, rgba(15,23,42,0.55), rgba(15,23,42,0.75)), url('${MOON_BG}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'scroll',
      }}
    >
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60" aria-hidden />

      <div
        className={cn(
          'relative z-10 flex w-full flex-col items-center px-4',
          compact ? 'justify-start pt-4 pb-2' : 'flex-1 justify-center pt-10'
        )}
      >
        <div className="text-center">
          <motion.div
            className="pointer-events-none mb-3 flex justify-center sm:mb-4"
            animate={{ y: [0, -6, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            aria-hidden
          >
            <SiriOrb
              size={compact ? '56px' : '104px'}
              animationDuration={compact ? 14 : 18}
              className="drop-shadow-[0_8px_24px_rgba(43,127,255,0.35)]"
            />
          </motion.div>
          <h1
            className={cn(
              'font-semibold text-white drop-shadow-sm',
              compact ? 'text-2xl' : 'text-4xl'
            )}
          >
            {heroTitle}
          </h1>
          <p
            className={cn(
              'text-neutral-200',
              compact ? 'mt-1 px-1 text-xs leading-snug' : 'mt-2 text-base'
            )}
          >
            {heroSubtitle}
          </p>
        </div>
      </div>

      <div
        className={cn(
          'relative z-10 w-full max-w-3xl shrink-0 px-4',
          compact ? 'mb-3 pb-3' : 'mb-8 pb-10 sm:mb-12'
        )}
      >
        <div className="relative rounded-xl border border-neutral-700 bg-black/60 backdrop-blur-md">
          <Textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              adjustHeight();
            }}
            onKeyDown={onKeyDown}
            placeholder="Type your request..."
            className={cn(
              'w-full resize-none border-none bg-transparent px-4 py-3 text-sm text-white',
              'min-h-[48px] placeholder:text-neutral-400',
              'focus-visible:ring-0 focus-visible:ring-offset-0'
            )}
            style={{ overflow: 'hidden' }}
          />

          <div className="flex items-center justify-between p-3">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-white hover:bg-neutral-700"
              aria-label="Attach file (coming soon)"
              disabled
            >
              <Paperclip className="h-4 w-4" />
            </Button>

            <div className="flex items-center gap-2">
              <Button
                type="button"
                disabled={!message.trim()}
                onClick={send}
                className={cn(
                  'flex items-center gap-1 rounded-lg px-3 py-2 transition-colors',
                  message.trim()
                    ? 'bg-[#2B7FFF] text-white hover:bg-[#1A6AE8]'
                    : 'cursor-not-allowed bg-neutral-700 text-neutral-400'
                )}
              >
                <ArrowUpIcon className="h-4 w-4" />
                <span className="sr-only">Send</span>
              </Button>
            </div>
          </div>
        </div>

        <div
          className={cn(
            'flex flex-wrap items-center justify-center gap-3',
            compact ? 'mt-3 max-h-[9.5rem] gap-2 overflow-y-auto overscroll-contain pr-0.5' : 'mt-6'
          )}
        >
          {BACKR_QUICK_PROMPTS.map(({ label, icon, prompt }) => (
            <QuickAction
              key={label}
              compact={compact}
              icon={icon}
              label={label}
              onClick={() => {
                onSubmit(prompt);
                setMessage('');
                adjustHeight(true);
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

interface QuickActionProps {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  compact?: boolean;
}

function QuickAction({ icon, label, onClick, compact = false }: QuickActionProps) {
  return (
    <Button
      type="button"
      variant="outline"
      size={compact ? 'sm' : 'default'}
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 rounded-full border-neutral-600 bg-black/50 text-neutral-200 hover:bg-neutral-700 hover:text-white',
        compact && 'h-8 px-2.5 text-[10px]'
      )}
    >
      {compact ? <span className="[&_svg]:size-3">{icon}</span> : icon}
      <span className={compact ? 'text-[10px]' : 'text-xs'}>{label}</span>
    </Button>
  );
}

export default RuixenMoonChat;
